import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCreateDNSPolicy } from "@/hooks/useDNSPolicies";
import { useToast } from "@/components/ui/use-toast";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Home,
  BarChart3,
  Shield,
  Users,
  Settings,
  Plus,
  Inbox,
  Search,
  FileText,
} from "lucide-react";

// Import the new quick action components
import { DomainManagementModal } from "./domain-management-modal";
import { DomainLookupTool } from "./domain-lookup-tool";
import { PolicyTemplateSystem } from "./policy-template-system";
import PolicyForm from "./forms/PolicyForm";

interface NavItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface QuickAction {
  title: string;
  action: () => void;
  icon: React.ComponentType<{ className?: string }>;
}

interface NavMainProps {
  items: NavItem[];
  quickActions?: QuickAction[];
}

export function NavSecondary() {
  // Modal states for quick actions
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showWhitelistModal, setShowWhitelistModal] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [showLookupTool, setShowLookupTool] = useState(false);
  const [showPolicySystem, setShowPolicySystem] = useState(false);

  // Hooks for policy creation
  const { toast } = useToast();
  const createPolicy = useCreateDNSPolicy();

  // Policy creation handler
  const handleCreatePolicy = async (formData: any) => {
    try {
      await createPolicy.mutateAsync(formData);
      setShowPolicyModal(false);
      toast({
        title: "Policy created",
        description: `Successfully created DNS policy "${formData.name}"`,
      });
    } catch (error) {
      console.error("Failed to create policy:", error);
      toast({
        title: "Error",
        description: "Failed to create policy. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Create quick actions with modal triggers
  const enhancedQuickActions: QuickAction[] = [
    {
      title: "Block Domain",
      action: () => setShowBlockModal(true),
      icon: Shield,
    },
    {
      title: "Whitelist Domain",
      action: () => setShowWhitelistModal(true),
      icon: Shield,
    },
    {
      title: "Create Policy",
      action: () => setShowPolicyModal(true),
      icon: FileText,
    },
    {
      title: "Domain Lookup",
      action: () => setShowLookupTool(true),
      icon: Search,
    },
  ];

  const handleQuickAction = (action: QuickAction) => {
    action.action();
  };

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem className="text-xs font-medium text-muted-foreground px-3 py-1">
          Quick Action
        </SidebarMenuItem>
        <SidebarMenuItem>
          <div className="space-y-2">
            {enhancedQuickActions.map((action, index) => (
              <Button
                key={index}
                variant="ghost"
                className="w-full justify-start text-sm h-auto py-2 px-3"
                onClick={() => handleQuickAction(action)}
              >
                <action.icon className="h-4 w-4 mr-2" />
                <div className="text-left">
                  <div className="font-medium">{action.title}</div>
                </div>
              </Button>
            ))}
          </div>
        </SidebarMenuItem>
      </SidebarMenu>

      {/* Quick Action Modals */}
      <DomainManagementModal
        isOpen={showBlockModal}
        onClose={() => setShowBlockModal(false)}
        action="block"
      />

      <DomainManagementModal
        isOpen={showWhitelistModal}
        onClose={() => setShowWhitelistModal(false)}
        action="whitelist"
      />

      {/* DNS Policy Creation Modal */}
      <Dialog open={showPolicyModal} onOpenChange={setShowPolicyModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] w-[95vw] sm:w-full bg-background/95 backdrop-blur-xl border border-primary/20 overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              Create New DNS Policy
            </DialogTitle>
          </DialogHeader>
          <PolicyForm
            onSubmit={handleCreatePolicy}
            onCancel={() => setShowPolicyModal(false)}
            isLoading={createPolicy.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Quick Action Tools */}
      {showLookupTool && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm">
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-6xl max-h-[90vh] overflow-y-auto bg-background border rounded-lg shadow-lg">
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-2xl font-bold">Domain Lookup Tool</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowLookupTool(false)}
                >
                  ✕
                </Button>
              </div>
              <div className="p-6">
                <DomainLookupTool />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
